/* nplus-buybox.js — <nplus-buybox> controller.
   Pill variant picker (syncs hidden select + price), Add-to-cart bundle (bike + gift + accessories),
   and the exclusive deposit/leasing accordion (wired in a later task). Classic script. */
(function () {
  'use strict';

  class NplusBuybox extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      this.select = this.querySelector('[data-nplus-bike-variant]');
      this.priceEl = this.querySelector('[data-nplus-price]');
      this.atc = this.querySelector('[data-nplus-atc]');
      this.errorEl = this.querySelector('[data-nplus-error]');
      this.gift = this.querySelector('nplus-gift-bundle');

      // Selective self-serve. Liquid decides eligibility and publishes a verdict per
      // variant; we only read it. Absent map (untagged product / kill switch off) means
      // no ATC markup rendered either, so applySelfServe() is a no-op.
      this.stock = this.parseStock();
      this.atcWrap = this.querySelector('[data-nplus-selfserve="atc"]');
      this.qualifyWrap = this.querySelector('[data-nplus-selfserve="qualify"]');

      var self = this;
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-variant-pill]'), function (pill) {
        pill.addEventListener('click', function () { self.selectPill(pill); });
      });
      if (this.atc) this.atc.addEventListener('click', this.onAdd.bind(this));

      // Stripe payment page (cash_mode = stripe_checkout). Liquid renders this button in
      // place of Add to cart, inside [data-nplus-selfserve="atc"], so it inherits the
      // product-level gate and the per-variant toggle.
      this.payBtn = this.querySelector('[data-nplus-stripe-pay]');
      if (this.payBtn) {
        this.payBtn.addEventListener('click', this.onStripePay.bind(this));
        // Back from Stripe via the browser's page cache: the button would still be busy.
        window.addEventListener('pageshow', function (e) { if (e.persisted) self.setPayBusy(false); });
      }
      this.showOrderNotice();

      // Cash / Finance "buy now" buttons: add the selection, go straight to checkout.
      // No Liquid renders this markup today. CONTRACT: if it returns, it must render
      // inside [data-nplus-selfserve="atc"] so it inherits the product-level gate and
      // the per-variant toggle. onBuyNow() guards on eligibility regardless.
      this.buyNow = Array.prototype.slice.call(this.querySelectorAll('[data-nplus-buynow]'));
      this.buyNow.forEach(function (btn) {
        btn.addEventListener('click', function () { self.onBuyNow(btn.getAttribute('data-method')); });
      });

      this.initReveals(); // accordion (defined below; no-op if no reveal buttons)
      this.initTabs();    // Cash/Lease/Finance switcher (no-op if no tabs)
      this.emitBikeVariant(); // initial bike size → size-linked accessories

      // Running total: bike price + selected paid accessories, in the presentment currency.
      this.priceFormatted = this.priceEl ? this.priceEl.textContent : '';
      this.bikeAmount = this.priceEl ? (parseInt(this.priceEl.getAttribute('data-amount'), 10) || 0) : 0;
      this.currency = this.priceEl ? this.priceEl.getAttribute('data-currency') : null;
      document.addEventListener('nplus:accessories-change', function () { self.updateTotal(); });
      this.updateTotal();
      this.applySelfServe();
    }

    selectPill(pill) {
      if (pill.getAttribute('aria-disabled') === 'true') return;
      var id = pill.getAttribute('data-variant');
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-variant-pill]'), function (p) {
        p.classList.toggle('is-selected', p === pill);
      });
      if (this.select) {
        this.select.value = id;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* URL API unavailable; skip */ }
      if (this.priceEl) {
        this.priceFormatted = pill.getAttribute('data-price') || this.priceFormatted;
        var amt = pill.getAttribute('data-price-amount');
        if (amt != null) this.bikeAmount = parseInt(amt, 10) || this.bikeAmount;
        this.updateTotal();
      }
      this.clearError();
      this.emitBikeVariant();
      this.applySelfServe();
    }

    // Reflect bike + selected paid accessories in the price line. With nothing paid
    // selected it shows the theme-formatted bike price (exact storefront money format);
    // once a paid accessory is added it shows the summed total via Intl in the
    // presentment currency (handles zero-decimal currencies like JPY automatically).
    updateTotal() {
      if (!this.priceEl) return;
      var paid = (window.nplusAccessories && typeof window.nplusAccessories.getPaidTotal === 'function')
        ? window.nplusAccessories.getPaidTotal() : 0;
      if (!paid || !this.currency) { this.priceEl.textContent = this.priceFormatted; this.refreshAffirm(); return; }
      this.priceEl.textContent = this.formatMoney(this.bikeAmount + paid, this.currency);
      this.refreshAffirm();
    }

    formatMoney(cents, currency) {
      try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(cents / 100); }
      catch (e) { return this.priceFormatted; }
    }

    // Notify size-linked accessories of the current bike size. Position is locale-stable
    // (variant.position), so accessories match on it rather than translated titles.
    // Cache on window covers either custom element connecting first.
    emitBikeVariant() {
      var pill = this.querySelector('[data-nplus-variant-pill].is-selected');
      var variantId = pill ? pill.getAttribute('data-variant') : (this.select ? this.select.value : null);
      var position = pill ? parseInt(pill.getAttribute('data-position'), 10) : 1;
      window.nplusBikePosition = position;
      document.dispatchEvent(new CustomEvent('nplus:bike-variant-change', { detail: { variantId: variantId, position: position } }));
    }

    // Per-variant self-serve verdict, rendered by Liquid. Fails closed: no map, no
    // entry, or a malformed map all read as ineligible (today's qualify-only behaviour).
    parseStock() {
      try {
        var el = this.querySelector('.nplus-bb__stock');
        return el ? (JSON.parse(el.textContent) || {}) : {};
      } catch (e) { return {}; }
    }

    isVariantEligible(id) {
      var entry = id != null && this.stock ? this.stock[String(id)] : null;
      return !!(entry && entry.eligible);
    }

    // Show Add to cart or the qualify form for the current variant. Size pills swap
    // variants without a section re-render, so this runs on every pill click.
    // No-op on products that rendered no ATC markup.
    applySelfServe() {
      if (!this.atcWrap || !this.qualifyWrap) return;
      var id = this.select ? this.select.value : null;
      var ok = this.isVariantEligible(id);
      this.atcWrap.hidden = !ok;
      this.qualifyWrap.hidden = ok;
      if (ok) this.closeQualifyPanel();
      // Stripe mode Cash | Finance tabs: only on an in-stock size. On any other size hide
      // the tab bar and fall back to the Cash panel, which then shows the enquiry form.
      var payTabs = this.querySelector('[data-nplus-paytabs]');
      if (payTabs) {
        payTabs.hidden = !ok;
        if (!ok && this.tabs && this.tabs.length) this.selectTab('cash');
      }
    }

    // Affirm's "as low as" message in the Finance tab: keep its amount on the current
    // total (bike + paid accessories, cents) and ask Affirm to redraw. No-op without Affirm.
    refreshAffirm() {
      var el = this.querySelector('[data-nplus-affirm]');
      if (!el) return;
      var paid = (window.nplusAccessories && typeof window.nplusAccessories.getPaidTotal === 'function')
        ? window.nplusAccessories.getPaidTotal() : 0;
      el.setAttribute('data-amount', String((this.bikeAmount || 0) + (paid || 0)));
      try {
        if (window.affirm && affirm.ui && typeof affirm.ui.ready === 'function') {
          affirm.ui.ready(function () { affirm.ui.refresh(); });
        }
      } catch (e) { /* Affirm not loaded */ }
    }

    // Collapse an open qualify accordion when the variant switches to Add to cart,
    // so it does not reappear mid-form on the next ineligible size.
    closeQualifyPanel() {
      var btn = this.querySelector('[data-nplus-reveal="qualify"]');
      var panel = this.querySelector('[data-nplus-panel="qualify"]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
    }

    setBusy(b) { if (this.atc) { this.atc.disabled = b; this.atc.setAttribute('aria-busy', b ? 'true' : 'false'); } }
    showError(m) { if (this.errorEl) { this.errorEl.textContent = m; this.errorEl.hidden = false; } }
    clearError() { if (this.errorEl) { this.errorEl.textContent = ''; this.errorEl.hidden = true; } }

    onAdd() {
      this.clearError();
      var bikeVariantId = this.select ? this.select.value : null;
      if (!bikeVariantId) { this.showError(this.t('select_option', 'Please select an option.')); return; }
      if (!this.isVariantEligible(bikeVariantId)) { this.showError(this.t('variant_unavailable', 'This size is not available for instant checkout.')); return; }
      if (!window.nplusCart || typeof window.nplusCart.addBundle !== 'function') { this.showError(this.t('cart_unavailable', 'Cart unavailable. Please refresh.')); return; }

      var gift = this.gift ? this.gift.getSelection() : { included: false, variantId: null };
      if (gift.included && !gift.variantId) { this.showError(this.t('choose_gift_size', 'Please choose a gift size.')); return; }

      var accessories = (window.nplusAccessories && typeof window.nplusAccessories.getSelection === 'function')
        ? window.nplusAccessories.getSelection() : [];

      this.setBusy(true);
      var self = this;
      window.nplusCart.addBundle({ bikeVariantId: bikeVariantId, gift: gift, accessories: accessories })
        .catch(function (err) {
          var sold = /no longer available|sold out/i.test(err.message || '');
          self.showError(sold ? self.t('gift_unavailable', 'That gift is no longer available. Deselect it to add the bike on its own.') : self.t('add_failed', 'Sorry, we could not add this to your cart. Please try again.'));
        })
        .then(function () { self.setBusy(false); });
    }

    // Pay on a Stripe-hosted page. Sends the SELECTION only (variant ids) — the backend
    // prices it from Shopify and returns the page URL, so nothing shown on this page
    // (price, total, "free") is trusted for the amount charged.
    checkoutEndpoint() {
      var host = window.location.hostname;
      var isLocal = host === 'localhost' || host === '127.0.0.1';
      if (isLocal && window.__BACKEND_BASE) return window.__BACKEND_BASE.replace(/\/$/, '') + '/create-checkout-session';
      return '/apps/nplus-deposit/create-checkout-session';
    }

    setPayBusy(b) { if (this.payBtn) { this.payBtn.disabled = b; this.payBtn.setAttribute('aria-busy', b ? 'true' : 'false'); } }

    onStripePay() {
      this.clearError();
      var bikeVariantId = this.select ? this.select.value : null;
      if (!bikeVariantId) { this.showError(this.t('select_option', 'Please select an option.')); return; }
      if (!this.isVariantEligible(bikeVariantId)) { this.showError(this.t('variant_unavailable', 'This size is not available for instant checkout.')); return; }

      var gift = this.gift ? this.gift.getSelection() : { included: false, variantId: null };
      if (gift.included && !gift.variantId) { this.showError(this.t('choose_gift_size', 'Please choose a gift size.')); return; }

      var selected = (window.nplusAccessories && typeof window.nplusAccessories.getSelection === 'function')
        ? window.nplusAccessories.getSelection() : [];
      var accessories = selected.map(function (a) { return { variantId: a.variantId }; });
      if (gift.included && gift.variantId) accessories.push({ variantId: gift.variantId });

      var self = this;
      this.setPayBusy(true);
      fetch(this.checkoutEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          variantId: bikeVariantId,
          accessories: accessories,
          returnPath: window.location.pathname + window.location.search,
          // UTMs, click IDs and the AvantLink avad, marketing consent only (nplus-attribution.js).
          // The Stripe page is not Shopify checkout, so this is the only way the sale gets attributed.
          attribution: window.nplusAttribution ? window.nplusAttribution.get() : null
        })
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (body) {
          if (!r.ok || !body || !body.url) throw new Error((body && body.error) || 'server_error');
          return body;
        });
      }).then(function (body) {
        window.location.assign(body.url);
      }).catch(function (err) {
        self.setPayBusy(false);
        var code = err && err.message;
        if (code === 'sold_out' || code === 'not_eligible') self.showError(self.t('pay_sold_out', 'Sorry, this size has just sold out.'));
        else if (code === 'accessory_sold_out') self.showError(self.t('pay_accessory_sold_out', 'One of the selected accessories has just sold out. Deselect it and try again.'));
        else self.showError(self.t('pay_failed', 'Sorry, we could not start the payment. Please try again.'));
      });
    }

    // Stripe returns the buyer to this PDP with ?nplus_order=success|cancelled. Show the
    // result once, then drop the param so a reload or a shared link does not repeat it.
    showOrderNotice() {
      var el = this.querySelector('[data-nplus-order-notice]');
      if (!el) return;
      var state = null;
      try {
        var url = new URL(window.location.href);
        state = url.searchParams.get('nplus_order');
        if (state) {
          url.searchParams.delete('nplus_order');
          window.history.replaceState({}, '', url.toString());
        }
      } catch (e) { return; }
      if (state === 'success') {
        el.textContent = this.t('order_success', 'Payment received. Thank you. Our team will be in touch by email with your order and shipping details.');
        el.classList.add('nplus-bb__notice--success');
      } else if (state === 'cancelled') {
        el.textContent = this.t('order_cancelled', 'Payment cancelled. You have not been charged.');
      } else { return; }
      el.hidden = false;
      try { el.scrollIntoView({ block: 'center' }); } catch (e) { /* older browsers */ }
    }

    // Cash/Finance "buy now": add bike + gift + accessories, then go straight to checkout
    // (no cart flyover). Both buttons (card/Klarna) land on the same checkout; Klarna is a
    // payment method there. `method` is a label/analytics hint only.
    onBuyNow(method) {
      this.clearError();
      var bikeVariantId = this.select ? this.select.value : null;
      if (!bikeVariantId) { this.showError(this.t('select_option', 'Please select an option.')); return; }
      if (!this.isVariantEligible(bikeVariantId)) { this.showError(this.t('variant_unavailable', 'This size is not available for instant checkout.')); return; }
      if (!window.nplusCart || typeof window.nplusCart.buildBundleItems !== 'function') { this.showError(this.t('cart_unavailable', 'Cart unavailable. Please refresh.')); return; }

      var gift = this.gift ? this.gift.getSelection() : { included: false, variantId: null };
      if (gift.included && !gift.variantId) { this.showError(this.t('choose_gift_size', 'Please choose a gift size.')); return; }

      var accessories = (window.nplusAccessories && typeof window.nplusAccessories.getSelection === 'function')
        ? window.nplusAccessories.getSelection() : [];

      var items = window.nplusCart.buildBundleItems({ bikeVariantId: bikeVariantId, gift: gift, accessories: accessories });
      var self = this;
      this.setBuyNowBusy(true);
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error((e && e.description) || 'add failed'); });
        return r.json();
      }).then(function () {
        var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        window.location.assign(root + 'checkout');
      }).catch(function (err) {
        self.setBuyNowBusy(false);
        var sold = /no longer available|sold out/i.test(err.message || '');
        self.showError(sold ? self.t('gift_unavailable', 'That gift is no longer available. Deselect it to add the bike on its own.') : self.t('add_failed', 'Sorry, we could not add this to your cart. Please try again.'));
      });
    }

    setBuyNowBusy(b) {
      (this.buyNow || []).forEach(function (btn) { btn.disabled = b; btn.setAttribute('aria-busy', b ? 'true' : 'false'); });
    }

    // Cash / Lease / Finance tab switcher. No-op on the reference buy-box (no tabs).
    initTabs() {
      var self = this;
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-nplus-tab]'));
      if (!this.tabs.length) return;
      this.tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { self.selectTab(tab.getAttribute('data-nplus-tab')); });
        tab.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          var next = (i + (e.key === 'ArrowRight' ? 1 : self.tabs.length - 1)) % self.tabs.length;
          self.tabs[next].focus();
          self.selectTab(self.tabs[next].getAttribute('data-nplus-tab'));
        });
      });
    }

    selectTab(name) {
      this.tabs.forEach(function (tab) {
        var on = tab.getAttribute('data-nplus-tab') === name;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.classList.toggle('is-active', on);
        tab.tabIndex = on ? 0 : -1;
      });
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-tabpanel]'), function (p) {
        p.hidden = p.getAttribute('data-nplus-tabpanel') !== name;
      });
      // Stripe is mounted on reveal (togglePanel), not on tab-select — the Lease/Finance
      // forms sit behind an "Order with…" reveal, so mounting here would mount while hidden.
      this.clearError();
      if (name === 'finance') this.refreshAffirm();
    }

    // i18n helper — reads optional cfg.i18n injected by Liquid (later task), else English fallback.
    t(key, fallback) {
      try {
        if (!this._i18n) {
          var el = this.querySelector('.nplus-bb__i18n');
          this._i18n = el ? JSON.parse(el.textContent) : {};
        }
        return this._i18n[key] || fallback;
      } catch (e) { return fallback; }
    }

    initReveals() {
      var self = this;
      this.reveals = Array.prototype.slice.call(this.querySelectorAll('[data-nplus-reveal]'));
      this.reveals.forEach(function (btn) {
        btn.addEventListener('click', function () { self.togglePanel(btn.getAttribute('data-nplus-reveal')); });
      });
    }

    togglePanel(name) {
      var self = this;
      var willOpen = false;
      this.reveals.forEach(function (btn) {
        var n = btn.getAttribute('data-nplus-reveal');
        var panel = self.querySelector('[data-nplus-panel="' + n + '"]');
        var open = (n === name) && !(btn.getAttribute('aria-expanded') === 'true');
        if (n === name) willOpen = open;
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (panel) panel.hidden = !open;
      });
      if (willOpen) {
        var openPanel = this.querySelector('[data-nplus-panel="' + name + '"]');
        var dep = openPanel && (openPanel.querySelector('nplus-leasing-deposit') || openPanel.querySelector('nplus-card-deposit'));
        if (dep && typeof dep.ensureStripe === 'function') dep.ensureStripe();
      }
    }
  }

  if (!customElements.get('nplus-buybox')) {
    customElements.define('nplus-buybox', NplusBuybox);
  }

  // "Pre-order only" info (main-product additional_info block, e.g. "Made to Order in 90
  // days"): visible only while the selected size has no stock on hand. Liquid sets the
  // first state and publishes the per-size quantities in data-stock; this keeps it right
  // when the size changes. Independent of self-serve, so it also works on untagged bikes.
  document.addEventListener('nplus:bike-variant-change', function (e) {
    var id = e && e.detail ? e.detail.variantId : null;
    if (id == null) return;
    var nodes = document.querySelectorAll('[data-nplus-preorder-only]');
    for (var i = 0; i < nodes.length; i++) {
      var qty = 0;
      try { qty = Number((JSON.parse(nodes[i].getAttribute('data-stock') || '{}') || {})[String(id)]) || 0; } catch (err) { qty = 0; }
      nodes[i].hidden = qty > 0;
    }
    // Stock on hand line (buy box): exact count for the selected size, hidden at zero.
    var lines = document.querySelectorAll('[data-nplus-onhand]');
    for (var j = 0; j < lines.length; j++) {
      var n = 0;
      try { n = Number((JSON.parse(lines[j].getAttribute('data-stock') || '{}') || {})[String(id)]) || 0; } catch (err2) { n = 0; }
      var textEl = lines[j].querySelector('[data-nplus-onhand-text]');
      if (n > 0 && textEl) {
        // Same rules as the Liquid in nplus-buybox.liquid: exact count (V17), or the
        // low-stock message at/under the threshold and the in-stock message above it.
        var tpl;
        if (lines[j].getAttribute('data-mode') === 'threshold') {
          var limit = Number(lines[j].getAttribute('data-threshold')) || 10;
          tpl = n <= limit ? lines[j].getAttribute('data-template-low') : lines[j].getAttribute('data-template-in');
        } else {
          tpl = lines[j].getAttribute('data-template');
        }
        textEl.textContent = (tpl || '').replace('__COUNT__', String(n));
      }
      lines[j].hidden = !(n > 0);
    }
  });
})();
